import { Box, Button, FormLabel, MenuItem, Select, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { useState } from "react";
import { integrationService } from "../../services/integrationService";
import type { Integration } from "../../types";

export const OAuthSetupForm: React.FC<{
  regions: { id: string; label: string }[];
  label?: string;
  integration: Integration;
  onSubmit: (updatedIntegration: Integration) => void;
}> = ({ integration, regions, onSubmit, label = "Connect" }) => {
  const [region, setRegion] = useState<string>("");
  const [err, setErr] = useState<string>("");

  const handleRegionChange = (e: SelectChangeEvent<string>) => {
    setRegion(e.target.value);
    if (err) {setErr("");}
  };

  return (
    <Box display="flex" gap={2.5} flexDirection="column">
      <Box display="flex" flexDirection="column">
        <FormLabel>Region</FormLabel>
        <Select
          size="medium"
          value={region}
          onChange={handleRegionChange}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {return "Select a region";}
            return regions.find((r) => r.id === selected)?.label ?? selected;
          }}
        >
          <MenuItem value="" disabled>
            Select a region
          </MenuItem>
          {regions.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Typography color="error">{err}</Typography>

      <Button
        onClick={async () => {
          if (!region) {
            setErr("Missing region");
            return;
          }

          const popup = window.open("about:blank", "_blank");
          if (!popup) {
            setErr("Please allow popups for this site and try again.");
            return;
          }

          try {
            const setupUrl = await integrationService.setupOAuth(integration.id, region);
            popup.location.replace(setupUrl);

            onSubmit({
              ...integration,
              auth: {
                type: "oauth",
                accessToken: "",
                refreshToken: "",
                ready: false,
                expiresAt: 0,
                region,
              },
            });
          } catch (e) {
            popup.close();
            console.error(e);
            setErr("Failed to start OAuth setup. Please try again.");
          }
        }}
        type="submit"
        variant="contained"
        startIcon={<LinkRoundedIcon fontSize="small" />}
      >
        {label}
      </Button>
    </Box>
  );
};
